import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useMemo } from 'react';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import ColumnSplittedLayout from '../../components/common/layout/ColumnSplittedLayout';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { ContentSidebarBookmarks } from '../../components/common/ui';
import Contact, { IContactProps } from '../../components/contact-us/Contact';
import CountryList from '../../components/countries/CountryList';
import { useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IApplicationClassPrice,
  IContactMethod,
  IContactUsForm,
  ICountryListPage,
  ICountryRegion,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMetaComponent,
  ITrademarkRegistrationCountryLanding,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  addCurrencySymbol,
  applyCurrencyConversionRate,
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getLocalizedSlugByCountryIso,
  getTranslation,
} from '../../shared/useUtils';
import { NextPageWithLayout } from '../_app';

interface ICountriesPageProps extends ILayoutPageProps {
  meta: IMetaComponent;
  heading: {
    title: string;
    subTitle: string;
  };
  countryRegions: {
    name: string;
    countries: {
      name: string;
      code: string;
      path: any;
      applicationClassPrices: IApplicationClassPrice[];
      services: { name?: string; price: string }[];
    }[];
  }[];
  trademarkLandings: ITrademarkRegistrationCountryLanding[];
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  trademarkRegistrationServiceName: string;
}

const CountriesPage: NextPageWithLayout<ICountriesPageProps> = (
  props: ICountriesPageProps
) => {
  const router = useRouter();
  const appState = useAppSelector((state) => state.app);

  const countryRegionsWithPrices = useMemo(() => {
    return props.countryRegions.map((region) => {
      region.countries = region.countries.map((country) => {
        const trademarkRegistrationPrice = applyCurrencyConversionRate(
          (country.applicationClassPrices[0] as IApplicationClassPrice).value,
          appState.currentCurrency?.conversionRate || 1
        );
        country.services = [
          {
            name: props.trademarkRegistrationServiceName,
            price: addCurrencySymbol(
              trademarkRegistrationPrice,
              appState.currentCurrency?.isoCode || 'USD',
              router.locale || 'en'
            ),
          },
        ];
        return country;
      });

      return region;
    });
  }, [
    appState.currentCurrency?.conversionRate,
    appState.currentCurrency?.isoCode,
    props.countryRegions,
    props.trademarkRegistrationServiceName,
    router.locale,
  ]);

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section is-narrow">
        <div id="countries-content" className="page-content-block">
          <CountryList
            title={props.heading.title}
            description={props.heading.subTitle}
            regions={countryRegionsWithPrices}
          />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          <Contact {...contactProps} />
        </div>
      </div>
    </div>
  );
};

CountriesPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      <ColumnSplittedLayout
        hideSidebarOnMobile
        sidebar={
          <ContentSidebarBookmarks
            bookmarkElementTagName={'h3'}
            contentElementId={'countries-content'}
          />
        }>
        {page}
      </ColumnSplittedLayout>
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ICountriesPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'title',
    'populate[2]': 'subTitle',
    'populate[3]': 'contactUsForm',
    'populate[4]': 'contactUsFormSuccessMessage',
    'populate[5]': 'contactUsFormErrorMessage',
    'populate[6]': 'trademarkRegistrationServiceName',
  });

  const regionsUrlParams = {
    'populate[0]': 'nameTranslations',
    'populate[1]': 'countries',
    'populate[2]': 'countries.nameTranslations',
    'populate[3]': 'countries.applicationClassPrices',
  };

  const trademarkLandingUrlParams = new URLSearchParams({
    'populate[0]': 'country',
    'populate[1]': 'meta',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const trademarkLandingsResult = await fetchStrapi<any>(
    `/trademark-registration-landings`,
    trademarkLandingUrlParams,
    buildApiKey
  );
  const trademarkLandings: ITrademarkRegistrationCountryLanding[] =
    flatStrapiCollection<ITrademarkRegistrationCountryLanding>(
      trademarkLandingsResult
    );

  if (trademarkLandings.length > 0) {
    trademarkLandings.forEach((x) => {
      x.country = flatStrapiObject(x.country as any);
    });
  }

  const page = (
    await fetchStrapi<any>(`/country-list-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ICountryListPage;
  const regions = flatStrapiCollection(
    await fetchStrapi<any>(`/country-regions`, regionsUrlParams, buildApiKey)
  ) as ICountryRegion[];

  regions.forEach((x) => {
    x.countries = flatStrapiCollection(x.countries as any);
  });

  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const commonTranslations = await getCommonTranslations();

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = getTranslation(
    page.title,
    context.locale
  ).text as string;

  // Prepare the regions and countries
  const mapCountryRegions = (
    regions: ICountryRegion[],
    trademarkLandings: ITrademarkRegistrationCountryLanding[],
    locale: string
  ) => {
    return regions
      .map((x) => ({
        order: x.order,
        name: x.nameTranslations.find((t) => t.language === locale)
          ?.text as string,
        countries: x.countries.map((c) => {
          const trademarkLandingSlug = getLocalizedSlugByCountryIso(
            trademarkLandings,
            c.iso,
            locale
          );
          return {
            name: getTranslation(c.nameTranslations, locale).text as string,
            code: c.iso,
            path: `${
              locale !== 'en' ? `/${locale}` : ''
            }/${trademarkLandingSlug}`,
            applicationClassPrices: c.applicationClassPrices,
            services: [],
          };
        }),
      }))
      .sort((a, b) => a.order - b.order);
  };

  const countryRegions = mapCountryRegions(
    regions,
    trademarkLandings,
    context.locale || 'en'
  );
  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/countries', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const props: ICountriesPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    heading: {
      title: getTranslation(page.title, context.locale).text as string,
      subTitle: getTranslation(page.subTitle, context.locale).text as string,
    },
    countryRegions,
    trademarkLandings,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage:
      getTranslation(page.contactUsFormSuccessMessage, context.locale).text ||
      '-',
    contactUsFormErrorMessage:
      getTranslation(page.contactUsFormErrorMessage, context.locale).text ||
      '-',
    contactMethods,
    alternativeLinks,
    canonicalLink,
    trademarkRegistrationServiceName: getTranslation(
      page.trademarkRegistrationServiceName,
      context.locale
    ).text as string,
  };

  return {
    props,
  };
}

export default CountriesPage;
